import React, { useEffect, useState, useMemo } from 'react'

import styled, { css } from 'styled-components'
import WithForcedMonospace from './WithForcedMonospace'
import { getFormattedDelta } from '../utils'

import { useStore } from '../zustandStore'

// const ANIMATION_DURATION_SECONDS = 12.5
const NUMBER_OF_BLINKS = 4 // because of how this is built, you actually get one more blink thank this


const WithAnimatedDeltas = ({
  children,
  delta,
  bestTime,
  updatedAt,
  chapterName = '',
  className = '',
  monospaceWidthEm,
  isFullGame,
  shouldTripleBlink = false,
  blinkTextAlign = 'right'
}) => {
  const BLINK_CYCLE_DURATION_MS = 2 * window.blinkHalfCycleMs
  const TOTAL_DURATION_MS = NUMBER_OF_BLINKS * BLINK_CYCLE_DURATION_MS * (isFullGame ? 5 : 2)

  const [isBlinking, setBlinking] = useState(false)
  const [blinkingStartedAtMs, setBlinkingStartedAtMs] = useState(null)
  const [onBlinkEndTimeoutId, setOnBlinkEndTimeoutId] = useState()
  const [windowValueOnBlinkEnd, setWindowValueOnBlinkEnd] = useState()

  const chapters = useStore(state => state.event.chapters)
  const disableBlinking = useStore((state) => state.publicAppSettings.blinkingAnimations === false)
  const isBingo = useStore((state) => state.event.isBingo)

  // // How recent does a time need to be to flash on first page load
  // const updatedAgeLimitSeconds = 850
  const updatedAgeLimitSeconds = isFullGame ? 120 : 30
  // const updatedAgeLimitSeconds = 60 * 10 * Math.random() * 6 
  // const updatedAgeLimitSeconds = 48 * 60 * 60 * 100000 

  const shouldBlink = () => {
    const updatedAgoSeconds = parseInt(Date.now()/1000) - updatedAt
    // console.log({updatedAgoSeconds, updatedAt, delta})
    // return (!!delta && delta[0] === '-')
    return (
      !!delta && delta[0] === '-'
      && updatedAt != null
      && updatedAgoSeconds <= updatedAgeLimitSeconds
    )
  }

  const shouldFinishBlinking = () => {
    if (!!delta && delta[0] === '-' && updatedAt != null && blinkingStartedAtMs != null) {
      const blinkingTurnedOffAgoMs = Date.now() - blinkingStartedAtMs - TOTAL_DURATION_MS

      // we let the global setInterval smoothly end the animation if it was turned off less than 1 cycle ago
      // if (isBlinking && blinkingTurnedOffAgoMs <= BLINK_CYCLE_DURATION_MS) {
      //   console.log('ima let you finish')
      // } else {
      //   console.log({blinkingTurnedOffAgoMs}, {BLINK_CYCLE_DURATION_MS})
      // }
      return (blinkingTurnedOffAgoMs <= BLINK_CYCLE_DURATION_MS)
    }
    return (false)
  }

  // console.log(TOTAL_DURATION_MS)
  useEffect(() => {
    if (shouldBlink()) {
      if (!isBlinking) {
        setOnBlinkEndTimeoutId(setTimeout(
          () => {
            setWindowValueOnBlinkEnd(shouldTripleBlink ? window.tripleBlink : window.blink)
            setBlinking(false)
            // console.log('removed blinking state')
          },
          (TOTAL_DURATION_MS)
        ))
        setBlinking(true)
        setBlinkingStartedAtMs(Date.now())
        // console.log('blinking enabled')
      } else {
        // console.log('should not see this uh oh')
        clearTimeout(onBlinkEndTimeoutId)
        setOnBlinkEndTimeoutId(setTimeout(
          () => {
            setWindowValueOnBlinkEnd(shouldTripleBlink ? window.tripleBlink : window.blink)
            setBlinking(false)
          },
          (TOTAL_DURATION_MS)
        ))
        setBlinking(true)
        setBlinkingStartedAtMs(Date.now())
      }
    } else {
      setBlinking(false)
    }
  }, [bestTime])


  if (children == null) return null

  if (isBingo || disableBlinking || (!isBlinking && !shouldFinishBlinking())) {
    // console.log('hard stop')
    return (
      <span>
        <span className={className}>{children}</span>
      </span>
    )
  }
  const displayedChapterName = (!isFullGame && !shouldTripleBlink) ? chapterName : null
  const formattedDelta = getFormattedDelta(delta, displayedChapterName)

  let emProps = {}
  if (monospaceWidthEm) {
    emProps['em'] = monospaceWidthEm
  }

  const styledBlinkingSpanClassName = shouldTripleBlink
    ? ' display-grid full-width'
    : ' blink-1 initial-off ' + (isBlinking
      ? 'blinking'
      : `blink-end ${windowValueOnBlinkEnd === 1 ? 'blink-on': 'blink-off'}`)

  const childrenWrapperSpanClassName = shouldTripleBlink
    ? ' triple-blink-0 initial-on' + (isBlinking
      ? ' triple-blinking'
      : ` triple-blink-end ${windowValueOnBlinkEnd === 0 ? 'blink-on': 'blink-off'}`)
    : ' blink-0 initial-on' + (isBlinking
      ? ' blinking'
      : ` blink-end ${windowValueOnBlinkEnd === 0 ? 'blink-on': 'blink-off'}`)

  const styledDeltaClassName = shouldTripleBlink
    ? ' triple-blink-1 initial-off animated delta full-width' + (isBlinking
      ? ` triple-blinking`
      : ` triple-blink-end ${windowValueOnBlinkEnd === 1 ? 'blink-on': 'blink-off'}`)
    : ''
  const styledChapterNameClassName = shouldTripleBlink
    ? ' triple-blink-2 initial-off animated full-width' + (isBlinking
      ? ` triple-blinking`
      : ` triple-blink-end ${windowValueOnBlinkEnd === 2 ? 'blink-on': 'blink-off'}`)
    : ''

  const chapterNameOverride = chapters[chapterName] && chapters[chapterName].nameOverrides.columnHeader

  return (
    <AnimationWrapper>
      <StyledBlinkingSpan className={className + styledBlinkingSpanClassName +' animated delta'}>
        <StyledDelta className={styledDeltaClassName} blinkTextAlign={blinkTextAlign}>
          <WithForcedMonospace {...emProps}>
            {formattedDelta}
          </WithForcedMonospace>
        </StyledDelta>

        {chapterName && !isFullGame && (
          <StyledChapterName
            isAbbreviated={!chapterNameOverride}
            className={styledChapterNameClassName}
            blinkTextAlign={blinkTextAlign}
          >
            {chapterNameOverride
              ? <>{chapterNameOverride}</>
              : <WithForcedMonospace {...emProps}>{chapterName}</WithForcedMonospace>
            }
          </StyledChapterName>
        )}
      </StyledBlinkingSpan>
      <ChildrenWrapperSpan className={className + childrenWrapperSpanClassName + ' animated'}>
        {children}
      </ChildrenWrapperSpan>
    </AnimationWrapper>
  )
}

export default WithAnimatedDeltas

const AnimationWrapper = styled.span`
  display: grid;
  ${'' /* color: red; */}
  z-index: 1;
  .animated {
    transition: opacity 350ms;

    grid-column: 1;
    grid-row: 1;

  }

  .initial-off { opacity: 0 }
  .initial-on { opacity: 1 }

  .blink-off { opacity: 0 }
  .blink-on { opacity: 1 }
`
const StyledDelta = styled.span`
  color: var(--time-saved-delta-color);
  text-align: ${props => props.blinkTextAlign || 'right'};
`
const StyledBlinkingSpan = styled.span`
  display: flex;
  justify-content: center;
  &.display-grid {
    display: grid !important;
  }
  &.full-width {
    grid-auto-columns: 1fr;
  }
`
const StyledChapterName = styled.span`
  ${'' /* font-size: 85%; */}
  ${props => props.isAbbreviated && css`
    text-transform: uppercase;
  `}

  color: #e1e1e1;
  text-align: ${props => props.blinkTextAlign || 'right'};
`
const ChildrenWrapperSpan = styled.span`
  ${'' /* animation: blink-off-on; */}
  ${'' /* &.blink-off { opacity: 0 }
  &.blink-on { opacity: 1 } */}
`
